<template>
  <div class="community-space-person-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('communitySpacePersonManage.page.title')"
      :subtitle="$t('communitySpacePersonManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-date-picker v-model="conditions.appointmentTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
            class="w-full" :placeholder="$t('communitySpacePersonManage.appointmentTimePlaceholder')" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.personName" clearable class="w-full"
            :placeholder="$t('communitySpacePersonManage.appointmentPersonPlaceholder')"
            @keyup.enter.native="_queryCommunitySpacePersonMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.personTel" clearable class="w-full"
            :placeholder="$t('communitySpacePersonManage.appointmentTelPlaceholder')"
            @keyup.enter.native="_queryCommunitySpacePersonMethod" />
        </el-col>
      </el-row>

      <!-- 更多查询条件 -->
      <div v-show="moreCondition">
        <el-row :gutter="16" class="ds-more-row">
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-select v-model="conditions.state" clearable class="w-full"
              :placeholder="$t('communitySpacePersonManage.statePlaceholder')">
              <el-option v-for="(value, key) in stateOptions" :key="key" :label="value" :value="key" />
            </el-select>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-select v-model="conditions.spaceId" clearable class="w-full"
              :placeholder="$t('communitySpacePersonManage.spacePlaceholder')">
              <el-option v-for="item in spaces" :key="item.spaceId" :label="item.name" :value="item.spaceId" />
            </el-select>
          </el-col>
        </el-row>
      </div>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryCommunitySpacePersonMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetCommunitySpacePersonMethod">
          {{ $t('common.reset') }}
        </el-button>
        <el-button type="text" icon="el-icon-more" @click="moreCondition = !moreCondition">
          {{ moreCondition ? $t('communitySpacePersonManage.hide') : $t('communitySpacePersonManage.more') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="refreshList">
        <template #left>
          <span class="ds-panel__title">{{ $t('communitySpacePersonManage.appointmentOrder') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="communitySpacePersons" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('communitySpacePersonManage.page.empty')" @retry="refreshList">
        <el-table-column prop="cspId" :label="$t('communitySpacePersonManage.orderId')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.cspId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="venueName" :label="$t('communitySpacePersonManage.venue')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.venueName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="spaceName" :label="$t('communitySpacePersonManage.space')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.spaceName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="personName" :label="$t('communitySpacePersonManage.appointmentPerson')" align="center"
          width="110">
          <template slot-scope="scope">
            {{ scope.row.personName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="personTel" :label="$t('communitySpacePersonManage.appointmentTel')" align="center"
          width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.personTel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="appointmentTime" :label="$t('communitySpacePersonManage.appointmentDate')" align="center"
          width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.appointmentTime || '-' }}</span>
          </template>
        </el-table-column>
        <!-- 预约时段：保留原有 hours + 状态 文案展示 -->
        <el-table-column :label="$t('communitySpacePersonManage.appointmentTime')" align="left" min-width="160">
          <template slot-scope="scope">
            <div v-if="!scope.row.times || scope.row.times.length === 0" class="ds-cell-sub">-</div>
            <div v-for="(time, index) in scope.row.times" :key="index" class="ds-time-item">
              <span class="ds-time-item__text">
                {{ time.hours }}{{ $t('common.hour') }}
                <span class="ds-time-item__state">
                  ({{ time.state === 'W' ? $t('communitySpacePersonManage.stateWaitConfirm') : $t('communitySpacePersonManage.stateConfirm') }})
                </span>
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('communitySpacePersonManage.receivableAmount')" align="right" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.receivableAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('communitySpacePersonManage.receivedAmount')" align="right" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.receivedAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="payWayName" :label="$t('communitySpacePersonManage.payWay')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.payWayName || '-' }}
          </template>
        </el-table-column>
        <!-- 状态 Tag：颜色仅作辅助，始终带文字（WCAG 1.4.1） -->
        <el-table-column prop="stateName" :label="$t('communitySpacePersonManage.state')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="resolveStateLabel(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('communitySpacePersonManage.createTime')" align="center"
          width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('communitySpacePersonManage.remark')" align="left" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('communitySpacePersonManage.operation')" align="center" width="110" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state === 'S'" type="text" size="small" class="ds-btn-danger"
              @click="_openDeleteCommunitySpacePersonModel(scope.row)">
              {{ $t('communitySpacePersonManage.cancelAppointment') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('communitySpacePersonManage.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 删除确认对话框 -->
    <delete-community-space-person ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import DeleteCommunitySpacePerson from '@/components/community/deleteCommunitySpacePerson'
import { listCommunitySpacePerson, listCommunitySpace } from '@/api/community/communitySpacePersonManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatMoney, formatDateTimeSec } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CommunitySpacePersonManageList',
  components: {
    DeleteCommunitySpacePerson,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      moreCondition: false,
      conditions: {
        spaceId: '',
        personName: '',
        personTel: '',
        appointmentTime: '',
        state: '',
        communityId: ''
      },
      communitySpacePersons: [],
      spaces: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      stateOptions: {
        'S': this.$t('communitySpacePersonManage.stateOptions.S'),
        'F': this.$t('communitySpacePersonManage.stateOptions.F'),
        'W': this.$t('communitySpacePersonManage.stateOptions.W'),
        'P': this.$t('communitySpacePersonManage.stateOptions.P')
      }
    }
  },
  created() {
    this.conditions.communityId = getCommunityId()
    this._listCommunitySpacePersonCommunitySpaces()
    this._listCommunitySpacePersons(this.page.current, this.page.size)
  },
  methods: {
    // 获取预约订单列表
    async _listCommunitySpacePersons(page, size) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.conditions,
          page,
          row: size
        }
        const res = await listCommunitySpacePerson(params)
        this.communitySpacePersons = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
        this.page.current = page
      } catch (error) {
        console.error('获取预约订单列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('communitySpacePersonManage.page.loadFailed')
        this.$message.error(error.message || this.$t('common.fetchError'))
      } finally {
        this.loading = false
      }
    },
    // 获取场地列表
    async _listCommunitySpacePersonCommunitySpaces() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.conditions.communityId
        }
        const res = await listCommunitySpace(params)
        this.spaces = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.$message.error(error.message || this.$t('common.fetchError'))
      }
    },
    /**
     * 刷新当前页（工具条刷新 / 错误态重试）
     */
    refreshList() {
      this._listCommunitySpacePersons(this.page.current, this.page.size)
    },
    // 查询
    _queryCommunitySpacePersonMethod() {
      this.page.current = 1
      this._listCommunitySpacePersons(this.page.current, this.page.size)
    },
    // 重置
    _resetCommunitySpacePersonMethod() {
      this.conditions = {
        spaceId: '',
        personName: '',
        personTel: '',
        appointmentTime: '',
        state: '',
        communityId: this.conditions.communityId
      }
      this._queryCommunitySpacePersonMethod()
    },
    // 打开删除确认模态框
    _openDeleteCommunitySpacePersonModel(row) {
      this.$refs.deleteDialog.open(row)
    },
    // 删除成功后刷新列表
    handleSuccess() {
      this._listCommunitySpacePersons(this.page.current, this.page.size)
    },
    // 分页大小改变
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listCommunitySpacePersons(this.page.current, val)
    },
    // 当前页改变
    handleCurrentChange(val) {
      this.page.current = val
      this._listCommunitySpacePersons(val, this.page.size)
    },
    /**
     * 金额格式化（只改展示，不改原值）
     */
    formatMoney(value) {
      return formatMoney(value)
    },
    /**
     * 创建时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    /**
     * 预约订单状态语义类型（community_space_person.state）：
     * S 预约成功 / F 预约失败 / W 待审核 / WP 待支付 / CL 取消预约
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'communitySpacePerson',
        text: row.stateName || ''
      }).type
    },
    /**
     * 预约订单状态文案：优先使用后端字典文案，缺失时回落到本地 i18n。
     */
    resolveStateLabel(row) {
      if (!row) return '-'
      if (row.stateName) return row.stateName
      const resolved = resolveStatus(row.state, {
        domain: 'communitySpacePerson',
        fallbackLabel: this.stateOptions[row.state] || ''
      })
      return resolved.label || row.state || '-'
    }
  }
}
</script>

<style lang="scss" scoped>
.community-space-person-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 更多条件行间距 */
.ds-more-row {
  margin-top: var(--ds-spacing-md);
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 预约时段条目 */
.ds-time-item {
  &__text {
    font-family: var(--ds-font-family-num);
    font-variant-numeric: tabular-nums;
    color: var(--ds-gray-700);
  }

  &__state {
    font-family: var(--ds-font-family);
    color: var(--ds-gray-500);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
